// ─────────────────────────────────────────────────────────────────────────────
// cloudStorageService.js — Cloud scenario backup + trainer invite gate
//
// Single-slot model: one scenario row per trainer in Supabase, always
// overwritten on Save. Maps exactly to the current localStorage model.
//
// All DB writes go through SECURITY DEFINER RPCs — no direct table access.
// All functions are async and throw on error — callers must try/catch.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from './supabaseClient';

export const cloudStorageService = {

  // ── Get (or auto-create) the trainer's profile ────────────────────────────
  // Returns { id, is_admin, is_trainer }.
  // Creates the profile row if this is the trainer's first login.
  // New rows default to is_trainer = false; invite redemption sets it true.
  async getProfile() {
    const { data, error } = await supabase.rpc('get_trainer_profile');
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data; // { id, is_admin, is_trainer }
  },

  // ── Save (upsert) the trainer's single scenario slot ─────────────────────
  // p_name: optional human-readable label, e.g. 'NATO_CORPS_2026-04-29'.
  // Non-blocking — callers should fire-and-forget with .catch(err => …).
  async upsertScenario(scenarioData, name) {
    const { data, error } = await supabase.rpc('upsert_scenario', {
      p_data: scenarioData,
      p_name: name || null,
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data; // { success: true, saved_at }
  },

  // ── Restore the trainer's cloud scenario ─────────────────────────────────
  // Returns { trainer_id, name, data, saved_at } or null if no backup exists.
  async getMyScenario() {
    const { data, error } = await supabase.rpc('get_my_scenario');
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data; // null | { trainer_id, name, data, saved_at }
  },

  // ── Admin: export all trainers' scenarios ────────────────────────────────
  // Throws { message: 'Not authorized — admin access required' } for non-admins.
  // Returns an array of { trainer_id, name, saved_at, data }.
  async adminExportAll() {
    const { data, error } = await supabase.rpc('admin_export_all_scenarios');
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data; // array of scenario objects
  },

  // ── TRAINER-GATE-001 — Trainer invite methods ─────────────────────────────

  // Validate a trainer invite token (works before authentication — anon RPC).
  // Returns { valid: true, note } or { valid: false, error }.
  async validateTrainerInvite(token) {
    const { data, error } = await supabase.rpc('validate_trainer_invite', {
      p_token: token,
    });
    if (error) throw error;
    return data; // { valid, note?, error? }
  },

  // Admin-only: generate a new trainer invite token.
  // Returns { token, expires_at, note }.
  async createTrainerInvite(note) {
    const { data, error } = await supabase.rpc('create_trainer_invite', {
      p_note: note || null,
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data; // { token, expires_at, note }
  },

  // Authenticated: consume invite token and set is_trainer = true on profile.
  // Returns { success: true } or throws.
  async redeemTrainerInvite(token) {
    const { data, error } = await supabase.rpc('redeem_trainer_invite', {
      p_token: token,
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data; // { success: true }
  },

  // Admin-only: list all trainer invites with status.
  // Returns array of { id, token, note, created_at, expires_at, used_at, used_by }.
  async getTrainerInvites() {
    const { data, error } = await supabase.rpc('get_trainer_invites');
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return Array.isArray(data) ? data : [];
  },

  // Admin-only: delete all expired (unused) trainer invite tokens.
  // Returns { deleted: N }.
  async clearExpiredTrainerInvites() {
    const { data, error } = await supabase.rpc('delete_expired_trainer_invites');
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  },
};
